import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import PageHeader from '@features/PageHeader';
import SubscribeToMessagesSnackbar from '@features/SubscribeToMessagesSnackbar';
import Button from '@components/Button';
import {
    channelsActions,
    conversationsActions,
    inboxActions,
    labelsActions,
    accountActions,
    agentsActions,
} from '@actions';
import { hubService } from '@services';
import { ConversationStatus, AssignedStatus, ConversationSorting } from '@constants/conversations';
import { WsHubMessage } from '@constants/ws-hub';
import { CHANNEL } from '@constants/channels';
import { MessageStatus, AssignmentType } from '@constants/inbox';
import { ROLE } from '@constants/user';
import { useTranslation, useQueryParams, useChannels, useConversations, useLocations } from '@hooks';
import { InboxEmptyIcon } from 'assets/images/icons';
import ContactInfo from './ContactInfo';
import Dialogs from './Dialogs';
import Inbox from './Inbox';
import { Container, PageGrid } from './styles';

const InboxPage = () => {
    const params = useQueryParams();
    const queryConversationId = params.get('conversationId');
    const queryConversationAssignment = params.get('assignment');

    const [filter, setFilter] = useState({
        channels: [],
        locations: [], // INFO: for API
        locationsValues: [], // INFO: ui state
        statuses: [ConversationStatus.Open],
        conversationId: queryConversationId || null,
        assignment: queryConversationAssignment ? +queryConversationAssignment : AssignedStatus.Mine,
        assignees: [],
        showUnreadOnly: false,
        timeframe: { start: null, end: null }, // INFO: ui state
        from: null, // INFO: for API
        to: null, // INFO: for API
        sorting: ConversationSorting.ActivityNewestFirst,
        assignedToPulse: false,
    });
    const [activeConversation, setActiveConversation] = useState(null);
    const [contactOpened, setContactOpened] = useState(false);

    const { data: messages } = useSelector(state => state.inbox);
    const { archived, archivedLoading } = useSelector(state => state.conversations);
    const { data: channels, loading: channelsLoading } = useSelector(state => state.channels);
    const currentUser = useSelector(state => state.auth.user);
    const users = useSelector(state => state.account?.account?.users || []);
    const locations = useSelector(state => state.account?.account?.companies || []);
    const { data: agents, loading: agentsLoading } = useSelector(state => state.agents);
    const { data: labels } = useSelector(state => state.labels);
    const { contactEditModal } = useSelector(state => state.contacts);

    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const isMobileAndContactOpened = useMediaQuery('@media (max-width: 1024px)');
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const { getChannelsByLocationsIds, checkTwoWaySms, checkWhatsAppStatus } = useChannels();
    const { conversationsRef, updateConversationInFeedAndReturn, updateConversationsChannelData } = useConversations();

    const firstRenderRef = useRef(true);
    const messagesRef = useRef(null);
    const activeConversationRef = useRef(null);
    const filterRef = useRef(null);
    const channelsRef = useRef([]);
    const delayedMessagesRef = useRef([]);

    const isUser = currentUser.role === ROLE.USER;

    useEffect(() => {
        /*
            INFO:It's necessary to prevent calling hubService.subscribeToEvents callbacks
            multiple times. Each time useEffect() is triggered, it will call hubService.subscribeToEvents,
            and all callbacks will be called the same number times. hub.off(event) method 
            removed each callback for event, so other subscribed components will not receive the event.
        */
        messagesRef.current = messages;
        // INFO: when messages change, update messages with delayed statuses
        if (delayedMessagesRef.current.length) {
            delayedMessagesRef.current.forEach(payload => {
                const { messageId, status } = payload;
                const messagesCopy = messagesRef.current.slice();
                const updatedMessageIndex = messagesCopy.findIndex(c => c.id === messageId);
                if (updatedMessageIndex !== -1) {
                    messagesCopy[updatedMessageIndex].status = status;
                    dispatch(inboxActions.update(messagesCopy));
                }
            });
            delayedMessagesRef.current = [];
        }
        activeConversationRef.current = activeConversation;
        filterRef.current = filter;
        channelsRef.current = channels;
    }, [messages, activeConversation, filter, channels]);

    useEffect(() => {
        const { channels: filterChannels, assignment, conversationId } = filterRef.current;
        // INFO: runs once initially after fetching channels
        if (!filterChannels.length && channels.length && !archivedLoading && !agentsLoading) {
            const locationsIds = locations.map(l => l.companyId);

            const filterCb = c =>
                [CHANNEL.LIVE_CHAT, CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM, CHANNEL.WHATSAPP_BUSINESS].includes(c?.type) ||
                checkTwoWaySms(c) ||
                checkWhatsAppStatus(c);
            const channelsIds = [
                ...getChannelsByLocationsIds(locationsIds, filterCb),
                ...(archived > 0 ? [CHANNEL.ARCHIVED] : []),
            ];
            // INFO:set users/agents ids and null (unassigned) for filter values
            const assignees =
                conversationId || assignment === AssignedStatus.All
                    ? [
                          ...users.map(u => ({ value: u.id, type: AssignmentType.User })),
                          { value: null, type: AssignmentType.Unassigned },
                          ...agents.map(a => ({ value: a.id, type: AssignmentType.Agent })),
                      ]
                    : assignment === AssignedStatus.Mine
                    ? [{ value: currentUser?.id, type: AssignmentType.User }]
                    : [{ value: null, type: AssignmentType.Unassigned }];
            // INFO: set to ALL if there is conversationId
            const updatedAssignment = conversationId ? AssignedStatus.All : assignment;
            const filterValues = {
                ...filterRef.current,
                channels: channelsIds,
                assignment: updatedAssignment,
                assignees,
                locations: locationsIds,
                locationsValues: mapLocationsToFormValue(),
            };
            setFilter(filterValues);
            dispatch(conversationsActions.getCounters(filterValues));
            dispatch(conversationsActions.get(filterValues));
            firstRenderRef.current = false;
        }
    }, [channels, archivedLoading, agentsLoading]);

    useEffect(() => {
        // INFO: need to update filter after redirect from activity/notifications if inbox is opened
        const { conversationId } = filterRef.current;
        if (!firstRenderRef.current && queryConversationId && queryConversationId !== conversationId) {
            const filterValues = {
                ...filterRef.current,
                conversationId: queryConversationId,
                assignment: AssignedStatus.All,
                assignees: [
                    ...users.map(u => ({ value: u.id, type: AssignmentType.User })),
                    { value: null, type: AssignmentType.Unassigned },
                    ...agents.map(a => ({ value: a.id, type: AssignmentType.Agent })),
                ],
            };
            setFilter(filterValues);
            dispatch(conversationsActions.get(filterValues));
        }
    }, [queryConversationId]);

    useEffect(() => {
        hubService.subscribeToEvents(
            {
                [WsHubMessage.InboxMessageCreated]: payload => {
                    if (!firstRenderRef.current) {
                        const { conversation, message } = payload;
                        const isActiveConversation = activeConversationRef.current?.id === conversation?.id;
                        const readConversation = isActiveConversation ? { ...conversation, read: true } : conversation;
                        // INFO: if selected conversation -> update inbox
                        if (isActiveConversation) {
                            setActiveConversation(prev => ({ ...prev, ...readConversation }));
                            dispatch(inboxActions.update([...messagesRef.current, message]));
                        }

                        const isConversationOld = updateConversationInFeedAndReturn(
                            readConversation,
                            filterRef.current,
                        );
                        if (isConversationOld) {
                            return;
                        }

                        // INFO: if conversation is new, add it to the list
                        dispatch(conversationsActions.update([...conversationsRef.current, readConversation]));
                        dispatch(conversationsActions.getCounters(filterRef.current));
                    }
                },
                [WsHubMessage.InboxMessageStatus]: payload => {
                    const { conversation, messageId, status } = payload;
                    // INFO: update message and selected conversation if conversation ids match
                    const isActiveConversation = activeConversationRef.current?.id === conversation?.id;
                    if (isActiveConversation) {
                        setActiveConversation(prev => ({ ...prev, ...conversation }));
                        const messagesCopy = messagesRef.current.slice();
                        const updatedMessageIndex = messagesCopy.findIndex(c => c.id === messageId);
                        if (updatedMessageIndex !== -1) {
                            messagesCopy[updatedMessageIndex].status = status;
                            dispatch(inboxActions.update(messagesCopy));
                        } else {
                            // INFO: put payload into queue to update later if message not found
                            delayedMessagesRef.current.push(payload);
                        }
                    }
                    // INFO: update conversation in feed
                    if (status === MessageStatus.Delivered) {
                        updateConversationInFeedAndReturn(conversation, filterRef.current);
                    }
                },
                [WsHubMessage.InboxChannelStatus]: async payload => {
                    const { channelId, isDeleted, channelStatus } = payload;
                    // INFO: update current conversation if channel matches
                    if (activeConversationRef?.current?.channelId === channelId) {
                        setActiveConversation(prev => ({
                            ...prev,
                            channelStatus,
                            channelId: isDeleted ? null : channelId,
                        }));
                    }
                    // INFO: update locations with channels
                    await dispatch(accountActions.get());
                    // INFO: updated conversations feed if channel is deleted or updated
                    updateConversationsChannelData(channelId, channelStatus, isDeleted);
                    // INFO: updated channels
                    const channelsCopy = channelsRef.current.slice();
                    const channelIndex = channelsCopy?.findIndex(c => c.id === channelId);
                    if (channelIndex !== -1) {
                        channelsCopy[channelIndex].status = channelStatus;
                        if (isDeleted) {
                            channelsCopy[channelIndex].id = null;
                            await dispatch(conversationsActions.getArchivedCounters());
                            // INFO: update channels in filter
                            setFilter(prev => ({ ...prev, channels: prev.channels.filter(id => id !== channelId) }));
                        }
                        dispatch(channelsActions.update(channelsCopy));
                    }
                },
            },
            () => {
                dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
                dispatch(conversationsActions.getCounters(filterRef.current));
                dispatch(inboxActions.getHistory(activeConversationRef.current?.id, false));
            },
        );
        return () => {
            hubService.unsubscribeFromEvents([
                WsHubMessage.InboxMessageCreated,
                WsHubMessage.InboxMessageStatus,
                WsHubMessage.InboxChannelStatus,
            ]);
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(conversationsActions.getArchivedCounters());
        dispatch(channelsActions.get());
        dispatch(agentsActions.get());
        dispatch(labelsActions.get());
        return () => {
            dispatch(conversationsActions.reset());
        };
    }, [dispatch]);

    useEffect(() => {
        // INFO: refetch conversations if contact labels are edited
        if (contactEditModal.open) {
            dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
        }
    }, [contactEditModal.open, labels]);

    const onCloseContact = () => setContactOpened(false);

    const onDeleteContactCallback = async () => {
        setContactOpened(false);
        setActiveConversation(null);
        await dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
        await dispatch(conversationsActions.getCounters(filterRef.current));
    };

    const onMergeContactCallback = (data, contactId) => {
        const updatedContact = {
            contactId: data?.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            phoneNumber: data?.phoneNumber,
            email: data?.email,
            labels: data?.labels,
        };
        const updatedConversations = conversationsRef.current.map(conversation => {
            return conversation.contactId === contactId || conversation.contactId === data?.id
                ? { ...conversation, ...updatedContact }
                : conversation;
        });
        dispatch(conversationsActions.update(updatedConversations));
        setActiveConversation(prev => ({ ...prev, ...updatedContact }));
    };

    return (
        <Container>
            {channels.length ? (
                <PageGrid contactIsOpen={contactOpened}>
                    {(!isMobile || !activeConversation) && !(isMobileAndContactOpened && contactOpened) && (
                        <Dialogs
                            isMobile={isMobile}
                            filter={filter}
                            setFilter={setFilter}
                            activeConversation={activeConversation}
                            setActiveConversation={setActiveConversation}
                            activeConversationRef={activeConversationRef}
                        />
                    )}
                    {(!isMobile || activeConversation) && (
                        <Inbox
                            isMobile={isMobile}
                            filterRef={filterRef}
                            setContactOpened={setContactOpened}
                            activeConversation={activeConversation}
                            setActiveConversation={setActiveConversation}
                        />
                    )}
                    {contactOpened ? (
                        <ContactInfo
                            open={contactOpened}
                            onClose={onCloseContact}
                            setActiveConversation={setActiveConversation}
                            onDeleteCallback={onDeleteContactCallback}
                            onMergeCallback={onMergeContactCallback}
                            onUpdateCallback={onMergeContactCallback}
                        />
                    ) : null}
                </PageGrid>
            ) : !channelsLoading ? (
                <PageHeader
                    icon={<InboxEmptyIcon />}
                    title={t('InboxPage.emptyChannelsTitle')}
                    subtitle={t('InboxPage.emptyChannelsSubtitle')}
                >
                    {!isUser && (
                        <Button variant="contained" onClick={() => history.push('/account-settings/channels')}>
                            {t('InboxPage.emptyChannelsButton')}
                        </Button>
                    )}
                </PageHeader>
            ) : null}
            <SubscribeToMessagesSnackbar />
        </Container>
    );
};

export default InboxPage;
